import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Button,
  Tag,
  Space,
  Typography,
  InputNumber,
  message,
  Alert,
  Divider,
  Empty,
  Popconfirm,
  Row,
  Col,
  Card,
  Statistic
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  InboxOutlined,
  WarningOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import { unifiedShipmentsAPI } from '../services/api.ts';
import { UnifiedShipment, UnifiedShipmentItem, UnifiedShipmentType } from '../types.ts';

const { Title, Text } = Typography;

interface ReadyProduct {
  product_id: number;
  product_code: string;
  product_description: string;
  supplier_order_id: number;
  supplier_order_number: string;
  supplier_id?: number;
  supplier_name?: string;
  supplier_order_detail_id: number;
  quantity_ready: number;
  quantity_already_shipped: number;
  quantity_remaining: number;
}

interface ShipmentProductsModalProps {
  visible: boolean;
  shipment: UnifiedShipment | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ShipmentProductsModal: React.FC<ShipmentProductsModalProps> = ({
  visible,
  shipment,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Prodotti già nella spedizione
  const [currentItems, setCurrentItems] = useState<UnifiedShipmentItem[]>([]);

  // Prodotti disponibili per aggiungere
  const [availableProducts, setAvailableProducts] = useState<ReadyProduct[]>([]);

  // Quantità selezionate per i prodotti disponibili
  const [selectedQuantities, setSelectedQuantities] = useState<Record<string, number>>({});

  useEffect(() => {
    if (visible && shipment) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, shipment]);

  const loadData = async () => {
    if (!shipment) return;

    setLoading(true);
    try {
      // Carica items correnti della spedizione
      const items = await unifiedShipmentsAPI.getShipmentItems(shipment.id);
      setCurrentItems(items || []);

      // Carica prodotti disponibili: tutti gli ordini pronti dei fornitori
      // compatibili con il tipo spedizione (spedizioni consolidate multi-fornitore)
      const readyOrders = await unifiedShipmentsAPI.getReadyToShipOrders({
        shipment_type: shipment.shipment_type
      });
      setAvailableProducts(readyOrders || []);

      // Precompila le quantità con il residuo da spedire
      // (chiave = riga d'ordine: lo stesso prodotto può comparire su più righe)
      const initialQuantities: Record<string, number> = {};
      (readyOrders || []).forEach((p: ReadyProduct) => {
        initialQuantities[String(p.supplier_order_detail_id)] = p.quantity_remaining;
      });
      setSelectedQuantities(initialQuantities);
    } catch (error: any) {
      console.error('Error loading data:', error);
      message.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (key: string, value: number | null) => {
    setSelectedQuantities(prev => ({
      ...prev,
      [key]: value || 0
    }));
  };

  const handleAddProduct = async (product: ReadyProduct) => {
    if (!shipment) return;

    const key = String(product.supplier_order_detail_id);
    const quantity = selectedQuantities[key] || 0;

    if (quantity <= 0) {
      message.warning('Inserisci una quantità valida');
      return;
    }

    if (quantity > product.quantity_remaining) {
      message.warning(`Quantità massima disponibile: ${product.quantity_remaining}`);
      return;
    }

    setSubmitting(true);
    try {
      await unifiedShipmentsAPI.addShipmentItem(shipment.id, [{
        product_id: product.product_id,
        supplier_order_id: product.supplier_order_id,
        supplier_order_detail_id: product.supplier_order_detail_id,
        quantity: quantity
      }]);

      message.success('Prodotto aggiunto alla Packing List');
      loadData();
      onSuccess();
    } catch (error: any) {
      console.error('Error adding product:', error);
      message.error(error.response?.data?.detail || 'Errore durante l\'aggiunta del prodotto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveItem = async (itemId: number) => {
    if (!shipment) return;

    setSubmitting(true);
    try {
      await unifiedShipmentsAPI.deleteShipmentItem(shipment.id, itemId);
      message.success('Prodotto rimosso dalla Packing List');
      loadData();
      onSuccess();
    } catch (error: any) {
      console.error('Error removing item:', error);
      message.error(error.response?.data?.detail || 'Errore durante la rimozione del prodotto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddAllProducts = async () => {
    if (!shipment) return;

    // Filtra solo prodotti con quantità disponibile > 0
    const productsToAdd = availableProducts.filter(p => p.quantity_remaining > 0);

    if (productsToAdd.length === 0) {
      message.warning('Nessun prodotto disponibile da aggiungere');
      return;
    }

    setSubmitting(true);
    try {
      // Prepara tutti gli items da aggiungere con la quantità massima disponibile
      const itemsToAdd = productsToAdd.map(product => ({
        product_id: product.product_id,
        supplier_order_id: product.supplier_order_id,
        supplier_order_detail_id: product.supplier_order_detail_id,
        quantity: product.quantity_remaining
      }));

      // Aggiungi tutti in una singola chiamata API
      await unifiedShipmentsAPI.addShipmentItem(shipment.id, itemsToAdd);

      message.success(`${productsToAdd.length} prodotti aggiunti alla Packing List`);
      loadData();
      onSuccess();
    } catch (error: any) {
      console.error('Error adding all products:', error);
      message.error(error.response?.data?.detail || 'Errore durante l\'aggiunta dei prodotti');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateQuantity = async (item: UnifiedShipmentItem, newQuantity: number) => {
    if (!shipment || newQuantity <= 0) return;

    setSubmitting(true);
    try {
      await unifiedShipmentsAPI.updateShipmentItem(shipment.id, item.id, {
        quantity: newQuantity
      });
      message.success('Quantità aggiornata');
      loadData();
      onSuccess();
    } catch (error: any) {
      console.error('Error updating quantity:', error);
      message.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento');
    } finally {
      setSubmitting(false);
    }
  };

  // Gli item sono modificabili solo se la spedizione è in preparazione (coerente col backend)
  const canEdit = shipment?.status_code === 'in_preparation';

  // Colonne per prodotti attuali nella spedizione
  const currentItemsColumns: ColumnsType<UnifiedShipmentItem> = [
    {
      title: 'Codice Prodotto',
      dataIndex: 'product_code',
      key: 'product_code',
      width: 120
    },
    {
      title: 'Descrizione',
      dataIndex: 'product_description',
      key: 'product_description',
      ellipsis: true
    },
    {
      title: 'Ordine Fornitore',
      dataIndex: 'supplier_order_number',
      key: 'supplier_order_number',
      width: 120
    },
    {
      title: 'Fattura',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      width: 120,
      render: (v: string | null) => v || '-'
    },
    {
      title: 'Quantità',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 150,
      render: (qty: number, record) => (
        <InputNumber
          min={1}
          value={qty}
          onChange={(value) => value && handleUpdateQuantity(record, value)}
          disabled={submitting || !canEdit}
          size="small"
        />
      )
    },
    {
      title: 'Peso Lordo',
      dataIndex: 'weight_gross',
      key: 'weight_gross',
      width: 100,
      render: (w: number) => w ? `${Number(w).toLocaleString('it-IT', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} kg` : '-'
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Popconfirm
          title="Rimuovere questo prodotto?"
          onConfirm={() => handleRemoveItem(record.id)}
          okText="Sì"
          cancelText="No"
          disabled={submitting || !canEdit}
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            disabled={submitting || !canEdit}
          />
        </Popconfirm>
      )
    }
  ];

  // Fornitori distinti tra i prodotti disponibili (per filtro colonna)
  const availableSuppliers = Array.from(
    new Set(availableProducts.map(p => p.supplier_name).filter(Boolean))
  ) as string[];

  // Colonne per prodotti disponibili
  const availableProductsColumns: ColumnsType<ReadyProduct> = [
    {
      title: 'Codice Prodotto',
      dataIndex: 'product_code',
      key: 'product_code',
      width: 120
    },
    {
      title: 'Descrizione',
      dataIndex: 'product_description',
      key: 'product_description',
      ellipsis: true
    },
    {
      title: 'Fornitore',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 160,
      ellipsis: true,
      filters: availableSuppliers.map(name => ({ text: name, value: name })),
      onFilter: (value, record) => record.supplier_name === value,
      render: (name: string) => name || '-'
    },
    {
      title: 'Ordine Fornitore',
      dataIndex: 'supplier_order_number',
      key: 'supplier_order_number',
      width: 120
    },
    {
      title: 'Disponibile',
      key: 'available',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color="green">{record.quantity_remaining} pz</Tag>
          {record.quantity_already_shipped > 0 && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              (già spediti: {record.quantity_already_shipped})
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'Quantità',
      key: 'quantity_to_add',
      width: 120,
      render: (_, record) => {
        const key = String(record.supplier_order_detail_id);
        return (
          <InputNumber
            min={0}
            max={record.quantity_remaining}
            value={selectedQuantities[key] || 0}
            onChange={(value) => handleQuantityChange(key, value)}
            disabled={submitting || record.quantity_remaining === 0 || !canEdit}
            size="small"
            style={{ width: 80 }}
          />
        );
      }
    },
    {
      title: '',
      key: 'action',
      width: 80,
      render: (_, record) => {
        const key = String(record.supplier_order_detail_id);
        const qty = selectedQuantities[key] || 0;
        return (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => handleAddProduct(record)}
            disabled={submitting || qty === 0 || record.quantity_remaining === 0 || !canEdit}
          >
            Aggiungi
          </Button>
        );
      }
    }
  ];

  // Calcola totali
  const totalItems = currentItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalWeight = currentItems.reduce((sum, item) => sum + (item.weight_gross || 0), 0);

  const getShipmentTypeLabel = (type: UnifiedShipmentType) => {
    switch (type) {
      case 'EXTRA_UE': return 'Extra-UE';
      case 'UE': return 'UE';
      case 'NAZIONALE': return 'Nazionale';
      default: return type;
    }
  };

  return (
    <Modal
      title={
        <span>
          <InboxOutlined style={{ marginRight: 8 }} />
          Packing List - {shipment?.shipment_code}
        </span>
      }
      open={visible}
      onCancel={onClose}
      width={1200}
      footer={[
        <Button key="close" onClick={onClose}>
          Chiudi
        </Button>
      ]}
    >
      {shipment && (
        <>
          {/* Info spedizione */}
          <Alert
            message={
              <Space>
                <Tag color={shipment.shipment_type === 'EXTRA_UE' ? 'blue' : shipment.shipment_type === 'UE' ? 'green' : 'orange'}>
                  {getShipmentTypeLabel(shipment.shipment_type)}
                </Tag>
                <Text strong>{shipment.description}</Text>
                <Text type="secondary">| Fornitore: {shipment.supplier_name || 'Multi-fornitore'}</Text>
              </Space>
            }
            type="info"
            style={{ marginBottom: 16 }}
          />

          {/* Statistiche */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Prodotti nella spedizione"
                  value={currentItems.length}
                  suffix="tipi"
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Quantità totale"
                  value={totalItems}
                  suffix="pz"
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Peso totale lordo"
                  value={totalWeight}
                  precision={4}
                  suffix="kg"
                />
              </Card>
            </Col>
          </Row>

          {!canEdit && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Packing List in sola lettura"
              description="La spedizione non è più in preparazione: i prodotti non sono modificabili (lo stock è già stato movimentato)."
            />
          )}

          {/* Prodotti nella spedizione */}
          <Divider orientation="left">
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
            Prodotti nella Packing List ({currentItems.length})
          </Divider>

          {currentItems.length > 0 ? (
            <Table
              columns={currentItemsColumns}
              dataSource={currentItems}
              rowKey="id"
              size="small"
              pagination={false}
              loading={loading}
              style={{ marginBottom: 24 }}
            />
          ) : (
            <Empty
              description="Nessun prodotto nella Packing List"
              style={{ marginBottom: 24 }}
            />
          )}

          {/* Prodotti disponibili */}
          <Row justify="space-between" align="middle" style={{ marginTop: 24, marginBottom: 16 }}>
            <Col>
              <Title level={5} style={{ margin: 0 }}>
                <PlusOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                Prodotti Disponibili da Aggiungere ({availableProducts.length})
              </Title>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddAllProducts}
                disabled={submitting || availableProducts.filter(p => p.quantity_remaining > 0).length === 0 || !canEdit}
                loading={submitting}
              >
                Aggiungi Tutti
              </Button>
            </Col>
          </Row>

          {availableProducts.length > 0 ? (
            <Table
              columns={availableProductsColumns}
              dataSource={availableProducts}
              rowKey={(record) => String(record.supplier_order_detail_id)}
              size="small"
              pagination={{ pageSize: 10 }}
              loading={loading}
            />
          ) : (
            <Empty
              description={
                <span>
                  <WarningOutlined style={{ marginRight: 8 }} />
                  Nessun prodotto disponibile per questa spedizione.
                  Verifica che ci siano ordini fornitore con stato "Pronto per Spedizione".
                </span>
              }
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default ShipmentProductsModal;
